/* eslint-disable react/jsx-no-undef */
/* eslint-disable linebreak-style */
import { useEffect, useRef, useState } from 'react';
import { Box, GlobalStyles } from '@mui/material';
import './shared/forms/TraducoesYup';
import { AppThemeProvider, AuthProvider, DrawerProvider } from './shared/contexts';
import { useAuthContext } from './shared/contexts/AuthContext';
import { Login } from './shared/components';
import { TelaEmpresaDesativada } from './shared/components/login/TelaEmpresaDesativada';
import { PDVScreen } from './pages/PDV/Pdv';

const FADE_MS = 400;

// Sem mais uma splash de logo com duração fixa entre o login e o PDV — o
// PDV entra direto e mostra seu próprio esqueleto (PDVLoadingSkeleton, ver
// Pdv.tsx) só se o carregamento do status do caixa demorar de verdade, em
// vez de sempre esperar uma animação de logo por tempo fixo.
const AppContent = () => {
  const { isAuthenticated, empresaInativa, logout } = useAuthContext();
  const [visible, setVisible] = useState(true);
  const wasAuthenticated = useRef(isAuthenticated);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Troca entre Login e PDV (nos dois sentidos) faz um fade rápido em vez
    // de trocar de tela abruptamente — sem flash, mas sem depender de mais
    // nenhum estado/temporizador de splash.
    if (isAuthenticated !== wasAuthenticated.current) {
      setVisible(false);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      fadeTimer.current = setTimeout(() => setVisible(true), FADE_MS);
      wasAuthenticated.current = isAuthenticated;
    }
  }, [isAuthenticated]);

  useEffect(() => () => {
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
  }, []);

  // Empresa desativada no painel de controle: tela de suporte no lugar do
  // login/PDV (igual ao FrontSIAC) — "Voltar para o login" limpa o aviso.
  const content = empresaInativa ? (
    <TelaEmpresaDesativada onVoltar={logout} />
  ) : isAuthenticated ? (
    <DrawerProvider>
      <PDVScreen />
    </DrawerProvider>
  ) : (
    <AppThemeProvider>
      <Login />
    </AppThemeProvider>
  );

  return (
    <Box
      sx={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${FADE_MS}ms ease`,
        minHeight: '100vh',
        backgroundColor: '#000',
      }}
    >
      {content}
    </Box>
  );
};

// App de tela única (Login ou PDV, nunca os dois, ver AppContent) — trava
// scroll em html/body/#root pra sempre: cada tela já controla sua própria
// altura (100dvh) e rolagem interna (só a listagem de itens, no PDV), então
// scroll do documento nunca deveria acontecer; sem essa trava, um erro de
// cálculo em qualquer tela vira scroll da página inteira em vez de ficar
// contido (e escondido) dentro do componente que errou.
const globalStyles = (
  <GlobalStyles
    styles={{
      'html, body, #root': {
        width: '100%',
        height: '100%',
        margin: 0,
        overflow: 'hidden',
      },
      // Valor padrão de --pdv-scale (1 = tamanho de referência, 1920×1080)
      // pro primeiro frame antes do useEffect de PDVScreen calcular o valor
      // real (ver pdvScale.ts) — sem isso, qualquer calc(Npx * var(--pdv-scale))
      // usado antes desse cálculo cairia num var() vazio/inválido.
      ':root': {
        '--pdv-scale': '1',
      },
    }}
  />
);

export const App = () => {
  useEffect(() => {
    const logo = new Image();
    logo.src = '/img/torqsiac-loading.png';
  }, []);

  return (
    <AuthProvider>
      {globalStyles}
      <AppContent />
    </AuthProvider>
  );
};
